'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useOpenStatusLabel } from './OpenStatusBadge'
import type { OpenStatus } from '@/lib/hours/openStatus'

type Props = {
  businessName: string
  tagline?: string
  heroImageUrl?: string
  heroVideoUrl?: string
  city?: string | null
  pickupWaitMinutes?: number
  readyInLabel?: string
  onViewMenu: () => void
  onDelivery?: () => void
  hasDelivery?: boolean
  openStatus?: OpenStatus | null
}

/** Menta de firma del tema ruta (damero, sombras duras del letrero). */
const MINT = '#8fd8c6'

/** Franja de damero — firma del tema ruta. */
function Checker() {
  return (
    <div
      className="h-2 w-full"
      style={{
        background:
          'repeating-linear-gradient(90deg, var(--ms-accent) 0 16px, var(--ms-bg) 16px 32px)',
      }}
      aria-hidden
    />
  )
}

/**
 * Hero del tema "ruta" (diner retro americana): letrero de carretera. Franjas
 * de damero arriba y abajo, nombre en slab de rótulo (Alfa Slab) con sombra
 * dura menta, tagline en bloque menta y foto enmarcada con borde grueso +
 * hard shadow menta. CTAs que se "hunden" al presionar. La barra superior usa
 * modo sólido (navSolid). Lee tokens del tema (--ms-*).
 */
export function HeroRuta({
  businessName,
  tagline,
  heroImageUrl,
  heroVideoUrl,
  city,
  pickupWaitMinutes,
  readyInLabel,
  onViewMenu,
  onDelivery,
  hasDelivery,
  openStatus,
}: Props) {
  const t = useTranslations('public.microsite')
  const statusLabel = useOpenStatusLabel(openStatus ?? null)
  const hasMedia = !!(heroImageUrl || heroVideoUrl)

  return (
    <section className="w-full overflow-hidden" style={{ background: 'var(--ms-bg)' }}>
      {/* mt despeja la barra superior fija (navSolid); el damero abre el letrero */}
      <div className="mt-[68px] sm:mt-[72px]">
        <Checker />
      </div>

      <div className="mx-auto max-w-4xl px-6 sm:px-8 pt-12 sm:pt-16 pb-12 text-center">
        {city && (
          <div
            className="text-[11.5px] font-bold uppercase tracking-[0.22em] mb-4"
            style={{ color: 'var(--ms-text-muted)' }}
          >
            {city}
          </div>
        )}

        <h1
          className="uppercase leading-[1.0] text-[clamp(42px,8.5vw,88px)]"
          style={{
            fontFamily: 'var(--ms-font-display)',
            color: 'var(--ms-accent)',
            textShadow: `3px 3px 0 ${MINT}`,
            letterSpacing: '0.01em',
          }}
        >
          {businessName}
        </h1>

        {tagline && (
          <p
            className="inline-block mt-5 px-4 py-1.5 text-[13.5px] sm:text-[14.5px] font-bold uppercase tracking-[0.06em] rounded-[6px]"
            style={{ background: MINT, color: 'var(--ms-text)' }}
          >
            {tagline}
          </p>
        )}

        {/* Meta discreta */}
        <div
          className="mt-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[13px] font-semibold"
          style={{ color: 'var(--ms-text-muted)' }}
        >
          {statusLabel && (
            <span className="inline-flex items-center gap-1.5">
              <span
                className="w-[6px] h-[6px] rounded-full"
                style={{
                  background:
                    statusLabel.tone === 'open'
                      ? '#2fbf9a'
                      : statusLabel.tone === 'soon'
                        ? '#f59e0b'
                        : 'var(--ms-text-muted)',
                }}
              />
              {statusLabel.text}
            </span>
          )}
          {statusLabel && <span aria-hidden>·</span>}
          <span>{t('pickupAvailable')}</span>
          {pickupWaitMinutes ? (
            <>
              <span aria-hidden>·</span>
              <span>{readyInLabel ?? t('readyIn', { minutes: pickupWaitMinutes })}</span>
            </>
          ) : null}
        </div>

        {/* CTAs con borde y hundimiento al presionar */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={onViewMenu}
            className="inline-flex items-center px-8 py-3.5 text-[14px] font-bold uppercase tracking-[0.06em] transition-transform active:translate-x-[3px] active:translate-y-[3px]"
            style={{
              background: 'var(--ms-accent)',
              color: '#ffffff',
              borderRadius: 'calc(var(--ms-radius) * 1.2)',
              border: '2px solid var(--ms-text)',
              boxShadow: '4px 4px 0 0 var(--ms-text)',
            }}
          >
            {t('orderOnline')}
          </button>
          {hasDelivery && onDelivery && (
            <button
              type="button"
              onClick={onDelivery}
              className="inline-flex items-center px-6 py-3.5 text-[14px] font-bold uppercase tracking-[0.06em] transition-colors hover:bg-[var(--ms-surface-2)]"
              style={{
                color: 'var(--ms-text)',
                borderRadius: 'calc(var(--ms-radius) * 1.2)',
                border: '2px solid var(--ms-text)',
              }}
            >
              {t('delivery')}
            </button>
          )}
        </div>
      </div>

      {/* Foto enmarcada estilo letrero: borde grueso + hard shadow menta */}
      {hasMedia && (
        <div className="mx-auto max-w-5xl px-6 sm:px-8 pb-14">
          <div
            className="relative w-full overflow-hidden"
            style={{
              aspectRatio: '16 / 7',
              border: '3px solid var(--ms-text)',
              borderRadius: 'calc(var(--ms-radius) * 1.6)',
              boxShadow: `8px 8px 0 0 ${MINT}`,
            }}
          >
            {heroImageUrl ? (
              <Image src={heroImageUrl} alt={businessName} fill priority sizes="100vw" className="object-cover" />
            ) : heroVideoUrl ? (
              <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" src={heroVideoUrl} />
            ) : null}
          </div>
        </div>
      )}

      <Checker />
    </section>
  )
}
